import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Paper,
    Typography,
    Button,
    Alert,
    TextField,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormControl,
    CircularProgress,
    Container,
    Table,
    TableHead,
    TableBody,
    TableRow,
    Select,
    MenuItem,
    IconButton,
    Collapse,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { api } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { StyledTableCell, StyledHeaderCell } from '../../components/StyledTableComponents';

interface FeedbackDimension {
    index: number;
    name: string;
    weight: number;
    questions: string[];
}

interface FeedbackTemplate {
    _id: string;
    name: string;
    dimensions: FeedbackDimension[];
}

interface FeedbackResponse {
    dimension: string;
    question: string;
    response: {
        score: number;
        response: string;
    };
    reason: string;
}

const SubmitFeedback: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { showToast } = useToast();

    const annualTargetId = searchParams.get('annualTargetId');
    const quarter = searchParams.get('quarter');
    const providerId = searchParams.get('providerId');

    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [feedbackTemplate, setFeedbackTemplate] = useState<FeedbackTemplate | null>(null);
    const [responses, setResponses] = useState<FeedbackResponse[]>([]);
    const [isEditing, setIsEditing] = useState<{ [key: string]: boolean }>({});
    const [previousResponses, setPreviousResponses] = useState<FeedbackResponse[]>([]);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [showValidationAlert, setShowValidationAlert] = useState(false);

    useEffect(() => {
        fetchFeedbackTemplate();
    }, [annualTargetId, quarter, providerId]);

    const fetchFeedbackTemplate = async () => {
        if (!annualTargetId || !quarter || !providerId) {
            setError('Invalid feedback link');
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            setError(null);

            // Get feedback template from public endpoint (no auth required)
            const response = await api.get(
                `/score-card/annual-targets/${annualTargetId}/ceo-feedback/${quarter}/submit-form/${providerId}`,
                {
                    headers: {
                        'Authorization': '' // No auth token needed
                    }
                }
            );
            
            if (response.data.status === 200 && response.data.data) {
                const template = response.data.data.feedbackTemplate;
                setFeedbackTemplate(template);
                
                // Initialize responses based on feedback dimensions
                const feedbackResponses = template.dimensions.flatMap(dimension =>
                    dimension.questions.map(question => ({
                        dimension: dimension.name,
                        question,
                        response: {
                            score: 0,
                            response: ''
                        },
                        reason: ''
                    }))
                ) as FeedbackResponse[];
                
                setResponses(feedbackResponses);
                setPreviousResponses(feedbackResponses);
            } else {
                setError('Feedback template not found');
            }

        } catch (error) {
            console.error('Error fetching feedback template:', error);
            setError('Failed to load feedback form');
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (dimensionName: string, questionIndex: number) => {
        const key = `${dimensionName}-${questionIndex}`;
        setPreviousResponses([...responses]);
        setIsEditing(prev => ({ ...prev, [key]: true }));
    };

    const handleCancel = (dimensionName: string, questionIndex: number) => {
        const key = `${dimensionName}-${questionIndex}`;
        setResponses(previousResponses);
        setIsEditing(prev => ({ ...prev, [key]: false }));
    };

    const handleSave = async (dimensionName: string, questionIndex: number) => {
        const key = `${dimensionName}-${questionIndex}`;
        setIsEditing(prev => ({ ...prev, [key]: false }));
    };

    const validateResponses = () => {
        const newErrors: { [key: string]: string } = {};
        let isValid = true;

        responses.forEach((response, index) => {
            const key = `${response.dimension}-${index}`;
            
            if (!response.response.score) {
                newErrors[`${key}-score`] = 'Response is required';
                isValid = false;
            }
            
            if (!response.reason.trim()) {
                newErrors[`${key}-reason`] = 'Reason is required';
                isValid = false;
            }
        });

        setErrors(newErrors);
        return isValid;
    };

    const isFormValid = () => {
        return responses.every(response =>
            response.response.score && response.reason.trim()
        );
    };

    const handleSubmit = async () => {
        if (!annualTargetId || !quarter || !providerId) {
            showToast('Invalid feedback link', 'error');
            return;
        }

        if (!validateResponses()) {
            setShowValidationAlert(true);
            return;
        }

        try {
            setSubmitting(true);

            // Convert responses to array format
            const responseArray = responses.map((resp, index) => {
                const dimensionIndex = feedbackTemplate!.dimensions.findIndex(d => d.name === resp.dimension);
                const questionIndex = feedbackTemplate!.dimensions[dimensionIndex].questions.findIndex(q => q === resp.question);
                
                return {
                    dimensionIndex,
                    questionIndex,
                    score: resp.response.score,
                    response: resp.reason
                };
            });

            const response = await api.post(
                `/score-card/annual-targets/${annualTargetId}/ceo-feedback/${quarter}/submit`,
                {
                    providerId,
                    responses: responseArray
                },
                {
                    headers: {
                        'Authorization': '' // No auth token needed
                    }
                }
            );

            if (response.data.status === 200) {
                showToast('Feedback submitted successfully!', 'success');
                setTimeout(() => {
                    navigate('/');
                }, 2000);
            }

        } catch (error: any) {
            console.error('Error submitting feedback:', error);
            showToast(error.response?.data?.message || 'Failed to submit feedback', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 3, maxWidth: 800, mx: 'auto', mt: 4 }}>
                <Alert severity="error">{error}</Alert>
            </Box>
        );
    }

    if (!feedbackTemplate) {
        return (
            <Box sx={{ p: 3, maxWidth: 800, mx: 'auto', mt: 4 }}>
                <Alert severity="warning">Feedback template not found</Alert>
            </Box>
        );
    }

    return (
        <Container maxWidth="xl">
            <Box sx={{
                p: 4,
                backgroundColor: '#fff',
                borderRadius: 2,
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
            }}>
                <Typography
                    variant="h5"
                    sx={{
                        mb: 4,
                        fontWeight: 600,
                        color: '#1F2937'
                    }}
                >
                    {feedbackTemplate.name}
                </Typography>

                <Collapse in={showValidationAlert}>
                    <Alert 
                        severity="error" 
                        onClose={() => setShowValidationAlert(false)}
                        sx={{ 
                            mb: 3,
                            '& .MuiAlert-message': {
                                fontSize: '0.875rem'
                            }
                        }}
                    >
                        Please complete all required fields before submitting. Required fields are marked with an error message.
                    </Alert>
                </Collapse>

                <Paper sx={{
                    mb: 4,
                    boxShadow: 'none',
                    border: '1px solid #E5E7EB',
                    borderRadius: 1,
                    overflow: 'hidden'
                }}>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: '#F9FAFB' }}>
                                <StyledHeaderCell width="20%">Feedback Dimension</StyledHeaderCell>
                                <StyledHeaderCell width="10%" align="center">Weight %</StyledHeaderCell>
                                <StyledHeaderCell width="20%">Feedback Question</StyledHeaderCell>
                                <StyledHeaderCell width="25%">Feedback Response</StyledHeaderCell>
                                <StyledHeaderCell width="15%">Feedback Reason</StyledHeaderCell>
                                <StyledHeaderCell width="10%" align="center">Action</StyledHeaderCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {feedbackTemplate.dimensions.map((dimension, dimIndex) =>
                                dimension.questions.map((question, qIndex) => {
                                    const key = `${dimension.name}-${qIndex}`;
                                    const response = responses.find(
                                        r => r.dimension === dimension.name && r.question === question
                                    );

                                    return (
                                        <TableRow
                                            key={key}
                                            sx={{
                                                '&:hover': {
                                                    backgroundColor: '#F9FAFB'
                                                }
                                            }}
                                        >
                                            {qIndex === 0 && (
                                                <>
                                                    <StyledTableCell
                                                        rowSpan={dimension.questions.length}
                                                        sx={{
                                                            fontWeight: 600,
                                                            color: '#1F2937'
                                                        }}
                                                    >
                                                        {dimension.name}
                                                    </StyledTableCell>
                                                    <StyledTableCell
                                                        align="center"
                                                        rowSpan={dimension.questions.length}
                                                        sx={{
                                                            fontWeight: 600,
                                                            color: '#1F2937'
                                                        }}
                                                    >
                                                        {dimension.weight}%
                                                    </StyledTableCell>
                                                </>
                                            )}
                                            <StyledTableCell>{question}</StyledTableCell>
                                            <StyledTableCell>
                                                {isEditing[key] ? (
                                                    <FormControl 
                                                        fullWidth 
                                                        size="small"
                                                        error={!!errors[`${key}-score`]}
                                                    >
                                                        <Select
                                                            value={response?.response.score || 0}
                                                            onChange={(e) => {
                                                                const updatedResponses = responses.map(r =>
                                                                    r.dimension === dimension.name && r.question === question
                                                                        ? { 
                                                                            ...r, 
                                                                            response: { 
                                                                                response: '', 
                                                                                score: e.target.value as number 
                                                                            } 
                                                                        }
                                                                        : r
                                                                );
                                                                setResponses(updatedResponses);
                                                                if (errors[`${key}-score`]) {
                                                                    setErrors(prev => {
                                                                        const newErrors = { ...prev };
                                                                        delete newErrors[`${key}-score`];
                                                                        return newErrors;
                                                                    });
                                                                }
                                                            }}
                                                            sx={{
                                                                '& .MuiOutlinedInput-notchedOutline': {
                                                                    borderColor: '#E5E7EB'
                                                                },
                                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                                    borderColor: '#D1D5DB'
                                                                },
                                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                    borderColor: '#0078D4'
                                                                },
                                                                height: '32px',
                                                                fontSize: '0.875rem'
                                                            }}
                                                        >
                                                            <MenuItem value={0} sx={{ fontSize: '0.875rem' }}>
                                                                Select a response
                                                            </MenuItem>
                                                            <MenuItem value={1} sx={{ fontSize: '0.875rem' }}>1 - Strongly Disagree</MenuItem>
                                                            <MenuItem value={2} sx={{ fontSize: '0.875rem' }}>2 - Disagree</MenuItem>
                                                            <MenuItem value={3} sx={{ fontSize: '0.875rem' }}>3 - Neutral</MenuItem>
                                                            <MenuItem value={4} sx={{ fontSize: '0.875rem' }}>4 - Agree</MenuItem>
                                                            <MenuItem value={5} sx={{ fontSize: '0.875rem' }}>5 - Strongly Agree</MenuItem>
                                                        </Select>
                                                        {errors[`${key}-score`] && (
                                                            <Typography 
                                                                variant="caption" 
                                                                color="error"
                                                                sx={{ 
                                                                    mt: 0.5,
                                                                    display: 'block'
                                                                }}
                                                            >
                                                                {errors[`${key}-score`]}
                                                            </Typography>
                                                        )}
                                                    </FormControl>
                                                ) : (
                                                    <Typography 
                                                        variant="body2" 
                                                        color={response?.response.score ? "text.primary" : "text.secondary"}
                                                        sx={{ 
                                                            minHeight: '32px',
                                                            display: 'flex',
                                                            alignItems: 'center'
                                                        }}
                                                    >
                                                        {response?.response.score 
                                                            ? `${response.response.score} - ${response.response.score === 1 ? 'Strongly Disagree' : response.response.score === 2 ? 'Disagree' : response.response.score === 3 ? 'Neutral' : response.response.score === 4 ? 'Agree' : 'Strongly Agree'}`
                                                            : 'Not selected'}
                                                    </Typography>
                                                )}
                                            </StyledTableCell>
                                            <StyledTableCell>
                                                {isEditing[key] ? (
                                                    <TextField
                                                        fullWidth
                                                        multiline
                                                        rows={1}
                                                        size="small"
                                                        value={response?.reason || ''}
                                                        onChange={(e) => {
                                                            const updatedResponses = responses.map(r =>
                                                                r.dimension === dimension.name && r.question === question
                                                                    ? { ...r, reason: e.target.value }
                                                                    : r
                                                            );
                                                            setResponses(updatedResponses);
                                                            if (errors[`${key}-reason`]) {
                                                                setErrors(prev => {
                                                                    const newErrors = { ...prev };
                                                                    delete newErrors[`${key}-reason`];
                                                                    return newErrors;
                                                                });
                                                            }
                                                        }}
                                                        error={!!errors[`${key}-reason`]}
                                                        helperText={errors[`${key}-reason`]}
                                                        sx={{
                                                            '& .MuiOutlinedInput-root': {
                                                                backgroundColor: '#fff',
                                                                fontSize: '0.875rem',
                                                                minHeight: '32px',
                                                                height: '32px'
                                                            }
                                                        }}
                                                    />
                                                ) : (
                                                    <Typography 
                                                        variant="body2" 
                                                        color={response?.reason ? "text.primary" : "text.secondary"}
                                                        sx={{ 
                                                            minHeight: '32px',
                                                            display: 'flex',
                                                            alignItems: 'center'
                                                        }}
                                                    >
                                                        {response?.reason || 'Not provided'}
                                                    </Typography>
                                                )}
                                            </StyledTableCell>
                                            <StyledTableCell align="center">
                                                {isEditing[key] ? (
                                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleSave(dimension.name, qIndex)}
                                                            sx={{
                                                                color: '#4CAF50',
                                                                '&:hover': {
                                                                    backgroundColor: 'rgba(76, 175, 80, 0.08)'
                                                                }
                                                            }}
                                                        >
                                                            <CheckIcon fontSize="small" />
                                                        </IconButton>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleCancel(dimension.name, qIndex)}
                                                            sx={{
                                                                color: '#F44336',
                                                                '&:hover': {
                                                                    backgroundColor: 'rgba(244, 67, 54, 0.08)'
                                                                }
                                                            }}
                                                        >
                                                            <CloseIcon fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                ) : (
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleEdit(dimension.name, qIndex)}
                                                        sx={{
                                                            color: '#6B7280',
                                                            '&:hover': {
                                                                color: '#1F2937',
                                                                backgroundColor: 'rgba(0,0,0,0.04)'
                                                            }
                                                        }}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                )}
                                            </StyledTableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </Paper>

                <Box sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 2,
                    mt: 4
                }}>
                    <Button
                        variant="outlined"
                        onClick={() => navigate('/')}
                        disabled={submitting}
                        sx={{
                            borderColor: '#D1D5DB',
                            color: '#374151',
                            '&:hover': {
                                borderColor: '#9CA3AF',
                                backgroundColor: 'rgba(0,0,0,0.04)'
                            }
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={submitting || !isFormValid()}
                        sx={{
                            backgroundColor: '#0078D4',
                            '&:hover': { 
                                backgroundColor: '#106EBE' 
                            },
                            boxShadow: 'none',
                            fontWeight: 600
                        }}
                    >
                        {submitting ? <CircularProgress size={24} color="inherit" /> : 'Submit'}
                    </Button>
                </Box>
            </Box>
        </Container>
    );
};

export default SubmitFeedback;

